import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Pressable,
  ScrollView,
  StyleSheet,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { useTheme } from '../context/ThemeContext';
import { fetchVisibleStudents } from '../utils/fetchVisibleStudents';

const LIGHT_CREAM = '#faf8f4';

function studentDisplayName(student) {
  return String(student?.student_name || student?.username || '').trim();
}

export default function NewClassLocationModal({
  visible,
  onClose,
  onCreated,
  currentUser,
}) {
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const [name, setName] = useState('');
  const [street, setStreet] = useState('');
  const [town, setTown] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [postcode, setPostcode] = useState('');
  const [defaultFeeAmount, setDefaultFeeAmount] = useState('');
  const [defaultFeeCurrency, setDefaultFeeCurrency] = useState('EUR');
  const [visibleStudents, setVisibleStudents] = useState([]);
  const [selectedStudentNames, setSelectedStudentNames] = useState(() => new Set());
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!visible) return;
    setError('');
    void (async () => {
      setLoadingStudents(true);
      try {
        const { students } = await fetchVisibleStudents({ requester: currentUser });
        setVisibleStudents(Array.isArray(students) ? students : []);
      } catch {
        setVisibleStudents([]);
      } finally {
        setLoadingStudents(false);
      }
    })();
  }, [visible, currentUser?.id]);

  const resetForm = () => {
    setName('');
    setStreet('');
    setTown('');
    setCity('');
    setCountry('');
    setPostcode('');
    setDefaultFeeAmount('');
    setDefaultFeeCurrency('EUR');
    setSelectedStudentNames(new Set());
    setError('');
  };

  const handleClose = () => {
    if (saving) return;
    resetForm();
    onClose?.();
  };

  const toggleStudent = (student) => {
    const label = studentDisplayName(student);
    if (!label) return;
    setSelectedStudentNames((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Name is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        Name: trimmedName,
        Street: street.trim() || null,
        Town: town.trim() || null,
        City: city.trim() || null,
        Country: country.trim() || null,
        Postcode: postcode.trim() || null,
        students: [...selectedStudentNames],
      };
      const feeTrimmed = defaultFeeAmount.trim();
      if (feeTrimmed) {
        const parsedFee = Number(feeTrimmed.replace(/,/g, ''));
        if (!Number.isFinite(parsedFee) || parsedFee < 0) {
          setError('Default class fee must be a valid amount');
          setSaving(false);
          return;
        }
        payload.default_fee_amount = parsedFee;
        payload.default_fee_currency = defaultFeeCurrency.trim().toUpperCase() || 'EUR';
      }
      await onCreated?.(payload);
      resetForm();
      onClose?.();
    } catch (e) {
      setError(e?.message || 'Failed to create class location');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={handleClose}>
      <View style={styles.modalRoot} data-screen="class-book-new-location-modal">
        <Pressable
          style={styles.backdrop}
          onPress={handleClose}
          {...(Platform.OS === 'web' ? { dataSet: { classBookNewLocationBackdrop: 'true' } } : {})}
        />
        <View style={styles.centerWrap} pointerEvents="box-none">
          <View
            style={styles.sheet}
            {...(Platform.OS === 'web' ? { dataSet: { classBookNewLocationSheet: 'true' } } : {})}
          >
            <Text style={styles.title}>New Class Location</Text>

            <ScrollView style={styles.formScroll} keyboardShouldPersistTaps="handled">
              {[
                { label: 'Name', value: name, setter: setName, required: true },
                { label: 'Street', value: street, setter: setStreet },
                { label: 'Town', value: town, setter: setTown },
                { label: 'City', value: city, setter: setCity },
                { label: 'Country', value: country, setter: setCountry },
                { label: 'Postcode', value: postcode, setter: setPostcode },
              ].map((field) => (
                <View key={field.label} style={styles.fieldBlock}>
                  <Text style={styles.label}>
                    {field.label}
                    {field.required ? ' *' : ''}
                  </Text>
                  <TextInput
                    style={styles.input}
                    value={field.value}
                    onChangeText={field.setter}
                    editable={!saving}
                    placeholder={field.label}
                    placeholderTextColor={theme.isDarkMode ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)'}
                    {...(Platform.OS === 'web'
                      ? { dataSet: { classBookNewLocationInput: 'true' } }
                      : {})}
                  />
                </View>
              ))}

              <View style={styles.fieldBlock}>
                <Text style={styles.label}>Default class fee</Text>
                <View style={styles.feeRow}>
                  <TextInput
                    style={[styles.input, styles.feeAmountInput]}
                    value={defaultFeeAmount}
                    onChangeText={setDefaultFeeAmount}
                    editable={!saving}
                    keyboardType="decimal-pad"
                    placeholder="0.00"
                    placeholderTextColor={
                      theme.isDarkMode ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)'
                    }
                    {...(Platform.OS === 'web'
                      ? { dataSet: { classBookNewLocationInput: 'true' } }
                      : {})}
                  />
                  <TextInput
                    style={[styles.input, styles.feeCurrencyInput]}
                    value={defaultFeeCurrency}
                    onChangeText={setDefaultFeeCurrency}
                    editable={!saving}
                    autoCapitalize="characters"
                    maxLength={3}
                    placeholder="EUR"
                    placeholderTextColor={
                      theme.isDarkMode ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)'
                    }
                    {...(Platform.OS === 'web'
                      ? { dataSet: { classBookNewLocationInput: 'true' } }
                      : {})}
                  />
                </View>
                <Text style={styles.hintText}>
                  Optional. Applied to new attendance rows from this date forward.
                </Text>
              </View>

              <Text style={styles.label}>Students</Text>
              {loadingStudents ? (
                <ActivityIndicator color={theme.colors.primary || '#d4af37'} />
              ) : (
                <View
                  style={styles.studentList}
                  {...(Platform.OS === 'web' ? { dataSet: { classBookNewLocationStudentList: 'true' } } : {})}
                >
                  {visibleStudents.map((student) => {
                    const label = studentDisplayName(student);
                    if (!label) return null;
                    const selected = selectedStudentNames.has(label);
                    return (
                      <TouchableOpacity
                        key={student.id}
                        style={styles.studentRow}
                        onPress={() => toggleStudent(student)}
                        disabled={saving}
                        {...(Platform.OS === 'web'
                          ? { dataSet: { classBookNewLocationStudentRow: 'true' } }
                          : {})}
                      >
                        <Text style={styles.studentCheck}>{selected ? '☑' : '☐'}</Text>
                        <Text style={styles.studentName}>{label}</Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              )}

              {error ? <Text style={styles.errorText}>{error}</Text> : null}
            </ScrollView>

            <View style={styles.btnRow}>
              <Pressable style={[styles.btn, styles.btnCancel]} onPress={handleClose} disabled={saving}>
                <Text style={styles.btnTextDark}>Cancel</Text>
              </Pressable>
              <Pressable style={[styles.btn, styles.btnSave]} onPress={() => void handleSave()} disabled={saving}>
                <Text style={styles.btnTextDark}>{saving ? 'Saving…' : 'Save'}</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );
}

function getStyles(theme) {
  return StyleSheet.create({
    modalRoot: {
      flex: 1,
      backgroundColor: 'transparent',
    },
    backdrop: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: 'rgba(0,0,0,0.55)',
    },
    centerWrap: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 20,
      backgroundColor: 'transparent',
    },
    sheet: {
      width: '100%',
      maxWidth: 520,
      maxHeight: '88%',
      borderRadius: 12,
      padding: 20,
      backgroundColor: theme.isDarkMode ? theme.colors.surface : LIGHT_CREAM,
      borderWidth: 1,
      borderColor: theme.colors.border,
    },
    title: {
      fontSize: 20,
      fontWeight: '700',
      color: theme.colors.text,
      marginBottom: 12,
      backgroundColor: 'transparent',
    },
    formScroll: {
      maxHeight: 420,
      backgroundColor: 'transparent',
    },
    fieldBlock: {
      marginBottom: 12,
      backgroundColor: 'transparent',
    },
    label: {
      fontSize: 14,
      fontWeight: '600',
      color: theme.colors.text,
      marginBottom: 6,
      backgroundColor: 'transparent',
    },
    input: {
      borderWidth: 1,
      borderColor: theme.colors.border,
      borderRadius: 8,
      paddingHorizontal: 12,
      paddingVertical: 10,
      fontSize: 16,
      color: theme.colors.text,
      backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
    },
    feeRow: {
      flexDirection: 'row',
      gap: 8,
      backgroundColor: 'transparent',
    },
    feeAmountInput: {
      flex: 1,
    },
    feeCurrencyInput: {
      width: 72,
      textAlign: 'center',
    },
    hintText: {
      marginTop: 4,
      fontSize: 12,
      color: theme.colors.text,
      opacity: 0.65,
      backgroundColor: 'transparent',
    },
    studentList: {
      borderWidth: 1,
      borderColor: theme.colors.border,
      borderRadius: 8,
      maxHeight: 180,
      marginBottom: 8,
      backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
    },
    studentRow: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: 10,
      paddingHorizontal: 12,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.border,
      backgroundColor: 'transparent',
    },
    studentCheck: {
      width: 28,
      fontSize: 20,
      color: theme.colors.primary || '#d4af37',
      backgroundColor: 'transparent',
    },
    studentName: {
      flex: 1,
      fontSize: 15,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    errorText: {
      color: theme.isDarkMode ? '#e57373' : '#c62828',
      marginTop: 8,
      backgroundColor: 'transparent',
    },
    btnRow: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      gap: 10,
      marginTop: 12,
      backgroundColor: 'transparent',
    },
    btn: {
      paddingVertical: 10,
      paddingHorizontal: 18,
      borderRadius: 8,
      minWidth: 92,
      alignItems: 'center',
    },
    btnCancel: {
      backgroundColor: '#B0B0B0',
    },
    btnSave: {
      backgroundColor: '#d4af37',
    },
    btnTextDark: {
      color: '#111',
      fontWeight: '700',
      backgroundColor: 'transparent',
    },
  });
}
